import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { CustomFieldModule } from '@ee/common/custom-field';
import { formatValidators } from '@ee/common/forms';
import { customFieldFor } from './field-view';
import { FieldDefinitionView, singularOf } from './intake-layout.model';

/** One row of a subject collection: a control per sub-field, validated as its definition says. */
export function subjectRowGroup(definition: FieldDefinitionView): FormGroup {
  const group = new FormGroup({});
  (definition.subject_fields ?? []).forEach((sub) => {
    const cf = customFieldFor(sub, { kind: 'FIELD', field_key: sub.key, span: 12 });
    group.addControl(
      sub.key,
      new FormControl(null, [...(cf.required ? [Validators.required] : []), ...formatValidators(cf.format)])
    );
  });
  return group;
}

/**
 * A party that repeats — tenants, defendants — drawn as one card per row of `ee-field`s.
 *
 * "Add" is always offered, even with no rows: a question about who is being evicted that shows an empty
 * box and no way in reads as broken. In the builder one inert row is drawn, so the canvas shows the shape.
 */
@Component({
  selector: 'ee-subject-group',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CustomFieldModule, MatIconModule],
  template: `
    <fieldset class="subject-group">
      <legend class="subject-group__legend" [class.is-visually-hidden]="legendHidden">{{ definition.label }}</legend>
      @for (row of rows; track row; let i = $index) {
        <div class="subject-group__card">
          <div class="subject-group__card-head">
            <span class="subject-group__card-title">{{ rowTitle(row, i) }}</span>
            @if (!builder && rows.length > 1) {
              <button
                type="button"
                class="ee__button gray-bg subject-group__remove"
                [attr.aria-label]="'Remove ' + rowTitle(row, i)"
                (click)="remove(i)">
                <mat-icon fontSet="fa" fontIcon="fa-trash"></mat-icon>
              </button>
            }
          </div>
          <div class="subject-group__fields">
            @for (sub of definition.subject_fields ?? []; track sub.key) {
              <ee-field
                class="subject-group__field"
                [field]="fieldFor(sub)"
                [control]="$any(row.get(sub.key))"
                [compact]="true"></ee-field>
            }
          </div>
        </div>
      }
      @if (!builder) {
        <button type="button" class="ee__button subject-group__add" (click)="add()">
          <mat-icon fontSet="fa" fontIcon="fa-plus"></mat-icon>
          <span>Add {{ singular }}</span>
        </button>
      }
    </fieldset>
  `,
  styleUrl: './subject-group.component.scss'
})
export class SubjectGroupComponent {
  @Input({ required: true }) definition!: FieldDefinitionView;

  /** The rows. Null in the builder, which draws its own inert row. */
  @Input() array: FormArray | null = null;

  @Input() builder = false;

  /**
   * The heading above already names this collection (the Tenants page holding only the tenants), so its
   * own label is kept for a screen reader, which needs the fieldset named, and hidden from view.
   */
  @Input() legendHidden = false;

  private inertRow: FormGroup | null = null;
  private readonly fields = new Map<string, ReturnType<typeof customFieldFor>>();

  protected get singular(): string {
    return this.definition.subject_label_singular || singularOf(this.definition.label);
  }

  /** A row is the person in it, once there is a name; until then, "Tenant 2". */
  protected rowTitle(row: FormGroup, index: number): string {
    const value = row.getRawValue() as Record<string, unknown>;
    const name = [value['legal_name']]
      .concat(value['legal_name'] ? [] : [value['first_name'], value['last_name']])
      .filter((part) => typeof part === 'string' && part.trim())
      .map((part) => (part as string).trim())
      .join(' ');
    return name || `${this.singular} ${index + 1}`;
  }

  protected get rows(): FormGroup[] {
    if (this.builder || !this.array) {
      this.inertRow ??= (() => {
        const group = subjectRowGroup(this.definition);
        group.disable({ emitEvent: false });
        return group;
      })();
      return [this.inertRow];
    }
    return this.array.controls as FormGroup[];
  }

  protected fieldFor(sub: FieldDefinitionView) {
    let field = this.fields.get(sub.key);
    if (!field) {
      field = customFieldFor(sub, { kind: 'FIELD', field_key: sub.key, span: 12 });
      this.fields.set(sub.key, field);
    }
    return field;
  }

  protected add(): void {
    this.array?.push(subjectRowGroup(this.definition));
  }

  protected remove(index: number): void {
    this.array?.removeAt(index);
  }
}
